import type { SVGProps } from 'react';

import { cn } from '../lib/cn';

/**
 * La marca Absor, dibujada en línea y no cargada como `<img>`.
 *
 * Los SVG publicados en el bucket de marca traen la palabra en blanco fijo, que
 * sobre el lienzo claro no se ve. Al inlinearlos, la palabra pasa a
 * `currentColor` —hereda la tinta del tema— y el degradado azul→cian del
 * logotipo se conserva tal como está publicado. Inlinear evita además que la
 * cabecera dependa de una red externa: este despliegue es privado.
 *
 * El `id` del degradado se genera por instancia porque dos marcas en la misma
 * página compartirían el `<defs>`, y la segunda acabaría pintando con el
 * degradado de la primera.
 */

const BRAND_FROM = '#0016ff';
const BRAND_MID = '#6577ff';
const BRAND_TO = '#00c9ff';

let seq = 0;
const nextId = () => `absor-grad-${(seq += 1)}`;

export function AbsorWordmark({ title, ...props }: { title?: string } & SVGProps<SVGSVGElement>) {
  const id = nextId();

  return (
    <svg
      viewBox="0 0 841.89 218.27"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      fill="none"
      {...props}
    >
      {title ? <title>{title}</title> : null}

      <defs>
        {/* Las coordenadas del degradado son las del fichero original: viven en
            el espacio del viewBox, no en el de la caja del elemento. */}
        <linearGradient id={id} x1="184.43" y1="220.94" x2="40.63" y2="77.13" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={BRAND_FROM} />
          <stop offset="1" stopColor={BRAND_TO} />
        </linearGradient>
      </defs>

      <path fill={`url(#${id})`} d="M135.1,29.05c6.58,4.05,11.2,9.87,13.89,17.43l53.54,156.35h-44.7l-41.66-136.89c-.35-1.19-.8-1.99-1.4-2.4-.6-.43-1.29-.64-2.14-.64s-1.56.25-2.16.76c-.58.49-1.05,1.25-1.38,2.28l-40.92,136.89H22.47L76.26,46.48c2.88-7.91,7.54-13.79,14.02-17.68,6.49-3.86,13.94-5.8,22.36-5.8s15.91,2.01,22.47,6.04Z" />
      <path fill={`url(#${id})`} d="M586.61,64.45l6.19,15.28,15.28,6.19c.15.06.15.26,0,.32l-15.28,6.19-6.19,15.28c-.06.15-.26.15-.32,0l-6.19-15.28-15.28-6.19c-.15-.06-.15-.26,0-.32l15.28-6.19,6.19-15.28c.06-.15.26-.15.32,0Z" />
      <path fill="currentColor" d="M297.66,202.83c-13.98,0-26.19-2.86-36.63-8.57-10.44-5.73-18.52-13.89-24.25-24.5-5.71-10.61-8.57-22.98-8.57-37.14V15.44h39.13v68.94h.76c2.53-3.54,5.69-6.6,9.48-9.21,3.78-2.61,8-4.58,12.62-5.94,4.62-1.34,9.56-2.01,14.78-2.01,12.29,0,23.06,2.77,32.33,8.32,9.25,5.57,16.55,13.24,21.85,23,5.3,9.76,7.95,21.21,7.95,34.35,0,10.09-1.6,19.44-4.81,28.04-3.19,8.57-7.79,16.03-13.75,22.34-5.98,6.31-13.22,11.16-21.73,14.53-8.51,3.35-18.23,5.04-29.17,5.04ZM297.66,169c6.06,0,11.26-1.48,15.66-4.42,4.38-2.94,7.77-6.99,10.22-12.13,2.45-5.14,3.66-10.89,3.66-17.31s-1.21-12.13-3.66-17.16c-2.45-5.06-5.84-9.06-10.22-12-4.4-2.94-9.6-4.42-15.66-4.42s-11.33,1.48-15.79,4.42c-4.46,2.94-7.87,6.95-10.24,12-2.36,5.04-3.54,10.77-3.54,17.16s1.17,12.17,3.54,17.31c2.36,5.14,5.78,9.19,10.24,12.13,4.46,2.94,9.72,4.42,15.79,4.42Z" />
      <path fill="currentColor" d="M414.56,199.81v-31.06h62.38c1.69,0,3.21-.39,4.54-1.15,1.36-.76,2.45-1.81,3.29-3.14.84-1.36,1.25-2.77,1.25-4.3,0-1.69-.41-3.21-1.25-4.54-.84-1.36-1.93-2.4-3.29-3.17-1.34-.76-2.86-1.13-4.54-1.13h-22.73c-8.43,0-16.03-1.4-22.86-4.17-6.82-2.77-12.21-7.15-16.16-13.13-3.97-5.98-5.94-13.77-5.94-23.37,0-7.4,1.81-14.14,5.43-20.2,3.62-6.06,8.59-10.93,14.9-14.63,6.31-3.72,13.26-5.57,20.84-5.57h62.38v31.32h-56.32c-2.36,0-4.34.76-5.94,2.28-1.6,1.5-2.4,3.37-2.4,5.55,0,2.36.8,4.34,2.4,5.94,1.6,1.6,3.58,2.38,5.94,2.38h22.22c9.43,0,17.55,1.4,24.38,4.17,6.82,2.77,12.13,7.19,15.91,13.26,3.78,6.06,5.69,13.89,5.69,23.49,0,7.58-1.91,14.49-5.69,20.72-3.78,6.23-8.84,11.2-15.15,14.9-6.31,3.7-13.44,5.55-21.36,5.55h-67.93Z" />
      <path fill="currentColor" d="M743.66,199.81v-91.42c0-11.8,3.35-21.05,10.09-27.79,6.74-6.72,16.07-10.09,28.04-10.09h37.63v31.82h-28.04c-2.36,0-4.38.8-6.06,2.4-1.69,1.6-2.53,3.66-2.53,6.19v88.89h-39.13Z" />
      <path fill="currentColor" d="M635.66,201.15c-36.31,0-65.85-29.54-65.85-65.85,0-8.36,6.76-15.12,15.12-15.12s15.12,6.76,15.12,15.12c0,19.63,15.98,35.6,35.6,35.6s35.6-15.98,35.6-35.6-15.98-35.59-35.6-35.59c-8.36,0-15.12-6.76-15.12-15.12s6.76-15.12,15.12-15.12c36.31,0,65.85,29.54,65.85,65.83s-29.54,65.85-65.85,65.85Z" />
    </svg>
  );
}

export function AbsorMark({ title, ...props }: { title?: string } & SVGProps<SVGSVGElement>) {
  const id = nextId();
  const maskId = `${id}-mask`;

  return (
    <svg
      viewBox="0 0 1284 1284"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      fill="none"
      {...props}
    >
      {title ? <title>{title}</title> : null}

      <defs>
        {/* Las coordenadas son las de la caja que ocupan las formas dentro del
            lienzo —el isotipo publicado deja mucho aire alrededor—, no las del
            lienzo entero: tendido sobre los 1284 el degradado gastaba en el
            aire casi todo su recorrido y al trazo solo le llegaba el arranque
            azul. */}
        <linearGradient id={id} x1="426" y1="376" x2="913" y2="863" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={BRAND_FROM} />
          <stop offset="0.62" stopColor={BRAND_MID} />
          <stop offset="1" stopColor={BRAND_TO} />
        </linearGradient>

        {/*
          El isotipo publicado trae las formas en blanco sobre un rectángulo
          negro. Aquí el fondo se queda fuera —quien lo use decide sobre qué se
          apoya— y las formas llevan el degradado de marca, que es lo que las
          hace reconocibles cuando no van dentro del cuadrado negro.

          Las formas recortan el degradado en vez de llevarlo puesto. Vienen del
          fichero original dentro de matrices que encogen su sistema de
          coordenadas a la unidad, y un degradado en `userSpaceOnUse` se
          resuelve en ESE sistema: puesto sobre el trazo, sus coordenadas
          quedaban lejísimos y el isotipo salía de un azul plano. Sobre el
          rectángulo se miden donde se definieron, que es el lienzo.
        */}
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="1284" height="1284">
          <g transform="translate(-3941.154 -2994.376)">
            <g transform="matrix(-502.674897,-502.674897,-502.674897,502.674897,4876.093565,3986.333306)">
              <path d="M0.995,-0.336C1.004,-0.3 1,-0.264 0.983,-0.226L0.626,0.504L0.467,0.346L0.801,-0.273C0.803,-0.277 0.804,-0.282 0.804,-0.285C0.804,-0.289 0.802,-0.293 0.799,-0.296C0.796,-0.299 0.793,-0.3 0.789,-0.301C0.786,-0.301 0.782,-0.3 0.776,-0.297L0.155,0.034L-0,-0.122L0.73,-0.479C0.765,-0.496 0.802,-0.5 0.839,-0.491C0.875,-0.483 0.909,-0.464 0.938,-0.434C0.967,-0.405 0.986,-0.372 0.995,-0.336Z" fill="#fff" fillRule="nonzero" />
            </g>
            <g transform="matrix(-76.096352,-76.096352,-76.096352,76.096352,4660.562655,3780.279548)">
              <path d="M0.999,-0.491L0.792,0L0.999,0.491C1.002,0.496 0.996,0.502 0.991,0.499L0.5,0.292L0.009,0.499C0.004,0.502 -0.002,0.496 0.001,0.491L0.208,-0L0.001,-0.491C-0.002,-0.496 0.004,-0.502 0.009,-0.499L0.5,-0.292L0.991,-0.499C0.993,-0.501 0.996,-0.5 0.998,-0.498C1,-0.496 1.001,-0.493 0.999,-0.491Z" fill="#fff" fillRule="nonzero" />
            </g>
          </g>
        </mask>
      </defs>

      <rect width="1284" height="1284" fill={`url(#${id})`} mask={`url(#${maskId})`} />
    </svg>
  );
}

/**
 * El logotipo completo tal y como se enseña: la palabra dibujada —que solo dice
 * «Absor»— seguida de «Staff» compuesto en la tipografía de la interfaz.
 *
 * Antes la cabecera ponía el isotipo a la izquierda del logotipo, y eso repetía
 * contenido: la «A» del isotipo es la misma «A» con la que arranca la palabra.
 * El isotipo se queda para donde va solo (el botón de la barra en móvil, el
 * asistente de empresa), y aquí la palabra va sin muleta.
 *
 * El tamaño se da con la tipografía —`text-lg`, `text-[1.75rem]`—, no con una
 * altura para el SVG: las dos mitades tienen que crecer juntas. De ahí las
 * medidas en `em`, sacadas del fichero original: las letras ocupan el 86 % de
 * la caja del `viewBox` y por debajo de la línea base queda un 7 % de aire, que
 * es lo que compensa el desplazamiento vertical para que «Absor» y «Staff» se
 * apoyen en la misma línea. La separación es de `0.02em` y no la de una palabra
 * porque el nombre es uno solo: con más aire se lee «Absor Staff».
 */
export function AbsorStaffLockup({ className }: { className?: string }) {
  return (
    <span
      role="img"
      aria-label="AbsorStaff"
      className={cn('inline-flex items-baseline gap-[0.02em] leading-none font-semibold tracking-tight', className)}
    >
      <AbsorWordmark className="h-[0.85em] w-auto translate-y-[0.06em]" />
      <span aria-hidden="true">Staff</span>
    </span>
  );
}
